import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Lightbulb, Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { TONI, formatoLabel } from "@/lib/ped";
import { ChannelPicker, ChannelIcons } from "./PedBits";

const L = ({ children }) => <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{children}</Label>;
const nextMonth = () => { const d = new Date(); d.setMonth(d.getMonth() + 1, 1); return format(d, "yyyy-MM"); };

export const IdeasDialog = ({ open, onOpenChange, client, onInserted }) => {
  const [mese, setMese] = useState(nextMonth());
  const [numero, setNumero] = useState(6);
  const [obiettivi, setObiettivi] = useState("");
  const [canali, setCanali] = useState(["instagram", "facebook"]);
  const [tono, setTono] = useState("amichevole");
  const [idee, setIdee] = useState([]);
  const [sel, setSel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !client) return;
    const social = ["instagram", "facebook", "linkedin", "tiktok", "youtube"].filter((c) => client[c]);
    setCanali(social.length ? social : ["instagram", "facebook"]);
    setIdee([]); setSel([]); setError("");
  }, [open, client]);

  const generate = async () => {
    if (!canali.length) return toast.error("Seleziona almeno un canale");
    const n = Number(numero);
    if (!n || n < 1 || n > 20) return toast.error("Il numero di post deve essere tra 1 e 20");
    setLoading(true); setError("");
    try {
      const r = await api.post("/ai/ideas", { client_id: client.id, mese, numero: n, obiettivi, canali, tono });
      setIdee(r.data.idee); setSel(r.data.idee.map((_, i) => i));
      if (r.data.idee.length < n) toast.info(`L'AI ha proposto ${r.data.idee.length} idee valide su ${n} richieste.`);
    } catch (e) { setError(errMsg(e, "Generazione non riuscita")); }
    finally { setLoading(false); }
  };

  const insert = async () => {
    setInserting(true);
    const posts = sel.map((i) => idee[i]).map((x) => ({
      titolo: x.tema, data_pubblicazione: new Date(`${x.data}T${x.ora}:00`).toISOString(), canali: [x.canale], formato: x.formato, testo: x.caption, hashtag: x.hashtag,
    }));
    try {
      const r = await api.post("/posts/bulk", { client_id: client.id, posts });
      toast.success(`${r.data.created} post inseriti come bozza${r.data.skipped ? ` (${r.data.skipped} scartati perché incompleti)` : ""}`);
      onOpenChange(false); onInserted?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setInserting(false); }
  };

  const toggle = (i) => setSel((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[94vh] overflow-y-auto rounded-2xl core-scroll" data-testid="ideas-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-[#0A1128] flex items-center gap-2"><Lightbulb className="h-6 w-6 text-[#E8173A]" />Idee PED con AI</DialogTitle>
          <DialogDescription>Claude Sonnet 5.5 propone un piano di post per {client?.ragione_sociale}. Seleziona le idee e inseriscile come bozza.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5"><L>Mese</L><Input type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="rounded-xl" data-testid="ideas-mese" /></div>
          <div className="space-y-1.5"><L>Numero di post</L><Input type="number" min={1} max={20} value={numero} onChange={(e) => setNumero(e.target.value)} className="rounded-xl" data-testid="ideas-numero" /></div>
          <div className="space-y-1.5"><L>Tono</L>
            <Select value={tono} onValueChange={setTono}>
              <SelectTrigger className="rounded-xl" data-testid="ideas-tono"><SelectValue /></SelectTrigger>
              <SelectContent>{TONI.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5"><L>Canali</L><ChannelPicker value={canali} onChange={setCanali} testidPrefix="ideas-canale" /></div>
        <div className="space-y-1.5"><L>Obiettivi del mese</L><Textarea rows={2} value={obiettivi} onChange={(e) => setObiettivi(e.target.value)} placeholder="Es. promuovere il nuovo menù, aumentare le prenotazioni del weekend" className="rounded-xl" data-testid="ideas-obiettivi" /></div>
        <Button onClick={generate} disabled={loading} className="rounded-full bg-[#0A1128] hover:bg-[#E8173A] font-bold w-fit" data-testid="ideas-generate-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{loading ? "Sto pianificando... (può richiedere fino a un minuto)" : idee.length ? "Rigenera idee" : "Genera idee"}
        </Button>
        {error && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-[#C8102E]" data-testid="ideas-error">{error}</div>}
        {idee.length > 0 && (
          <div className="space-y-2" data-testid="ideas-list">
            {idee.map((x, i) => (
              <label key={i} className={`flex gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${sel.includes(i) ? "border-[#0A1128]/40 bg-[#A6DAFC]/60" : "border-slate-200 bg-[#A6DAFC]"}`} data-testid={`idea-${i}`}>
                <Checkbox checked={sel.includes(i)} onCheckedChange={() => toggle(i)} className="mt-1" data-testid={`idea-check-${i}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                    <span className="capitalize">{format(parseISO(x.data), "EEE d MMM", { locale: it })} · {x.ora}</span>
                    <ChannelIcons canali={[x.canale]} size={12} /><span>{formatoLabel(x.formato)}</span>
                  </div>
                  <div className="font-bold text-slate-900 mt-0.5">{x.tema}</div>
                  <div className="text-sm text-slate-600 mt-1 line-clamp-3 whitespace-pre-wrap">{x.caption}</div>
                  {x.hashtag && <div className="text-xs font-semibold text-[#0A1128] mt-1 truncate">{x.hashtag}</div>}
                </div>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="ideas-cancel">Chiudi</Button>
          {idee.length > 0 && (
            <Button onClick={insert} disabled={!sel.length || inserting} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold px-6" data-testid="ideas-insert-button">
              {inserting ? "Inserimento..." : `Inserisci ${sel.length} post come bozza`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
